import React from 'react';
import styled, { FlattenSimpleInterpolation } from 'styled-components';
import { ParsedListing } from '@bb/domain/dist/types';
import { ListingImage } from '../ListingImage';
import { SkeletonButton } from '../Skeleton';
import PreviewButton from '../PreviewButton';
import Tooltip from '../Tooltip';
import theme from '../theme';

const height = 45;
const width = 62;

const StyledImgWrapper = styled.div<{
  cssOverride?: FlattenSimpleInterpolation;
}>`
  border-radius: 8px;
  overflow: visible;
  position: relative;
  width: ${width}px;
  height: ${height}px;
  background-color: ${theme.gray3};
  display: flex;
  align-items: center;
  justify-content: center;
  > .thumbnail-skeleton {
    span {
      height: ${height}px;
    }
  }
  ${({ cssOverride }) => cssOverride || ''}
`;

const StyledPreviewButton = styled(PreviewButton)`
  background: ${theme.gray9};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s;
  z-index: 2;
  > svg {
    fill: white;
    top: 0px;
  }
  &:hover {
    opacity: 0.8;
  }
`;

const IconOverlay = styled.div<{ size?: number }>`
  position: absolute;
  bottom: -8px;
  right: 4px;
  z-index: 3;
  width: ${props => props.size || 16}px;
  height: ${props => props.size || 16}px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;
export interface ThumbnailProps
  extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    'width' | 'height' | 'loading'
  > {
  loading?: boolean;
  listing?: Partial<ParsedListing>;
  wrapperStyle?: FlattenSimpleInterpolation;
  isPreviewDisabled?: boolean;
  overlayIcon?: React.ReactNode;
  overlayIconSize?: number;
  overlayIconColor?: string;
}

export const Thumbnail: React.FC<ThumbnailProps> = ({
  src,
  loading,
  listing,
  wrapperStyle,
  isPreviewDisabled,
  overlayIcon,
  overlayIconSize,
  overlayIconColor,
  ...props
}) => {
  if (loading) {
    return (
      <StyledImgWrapper cssOverride={wrapperStyle}>
        <div className="thumbnail-skeleton">
          <SkeletonButton />
        </div>
      </StyledImgWrapper>
    );
  }

  return (
    <StyledImgWrapper cssOverride={wrapperStyle}>
      {listing && (
        <Tooltip
          title={
            isPreviewDisabled &&
            'You cannot view this modal as this listing is not represented by BrokerBay.'
          }
          placement="left"
        >
          <div
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
              e.stopPropagation()
            }
          >
            <StyledPreviewButton
              hideStyling
              listing={listing}
              isDisabled={isPreviewDisabled}
            />
          </div>
        </Tooltip>
      )}
      <ListingImage imageSrc={src} size={width} />
      {overlayIcon && (
        <IconOverlay size={overlayIconSize}>{overlayIcon}</IconOverlay>
      )}
    </StyledImgWrapper>
  );
};

export default styled(Thumbnail)``;
